import React, {useEffect, useState} from 'react';
import {useQuery, useMutation} from '@apollo/client';
import {DELETE_TENANT, GET_TENANTS} from "../graphql/tenants";
import {ListComponent} from "../components/ListComponent";
import {FilterBar} from "../components/FilterBar";
import people from "../assets/images/people-black.svg";
import {CardComponent} from "../components/CardComponent";
import {ListRowComponent} from "../components/ListRowComponent";
import {PersonCardContentComponent} from "../components/PersonCardContentComponent";
import {useNavigate} from 'react-router-dom';

const Tenants = () => {
    const navigate = useNavigate();

    const [searchTerm, setSearchTerm] = useState('');
    const [tenants, setTenants] = useState({});
    const [sortBy, setSortBy] = useState('lastName');
    const [viewMode, setViewMode] = useState('grid');
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [activePill, setActivePill] = useState('all');

    const buildFilter = () => {
        const f = {
            isActive: true,
            isActiveTenant: true,
        };

        if (searchTerm) {
            f.or = [
                { firstName: { regex: searchTerm } },
                { lastName: { regex: searchTerm } },
                { email: { regex: searchTerm } },
            ];
        }

        if (activePill === 'ficaComplete') f.hasFicaDocument = true;
        else if (activePill === 'ficaPending') f.hasFicaDocument = { ne: true };
        else if (activePill === 'hasBanking') f.bankingDetails = { exists: true };
        else if (activePill === 'noBanking') f.bankingDetails = { exists: false };

        return f;
    };

    const query = useQuery(GET_TENANTS,{
        variables: {
            pageIndex,
            pageSize,
            orderAsc: true,
            orderField: "lastName",
            filter: { isActive: true }
        },
        onCompleted: (data) => {
            setTenants(data?.tenants);
        }
    });

    useEffect(() => {
        const fetchData = async () => {
            const {data} = await query.refetch({
                pageIndex,
                pageSize,
                orderAsc: true,
                orderField: sortBy,
                filter: buildFilter()
            });
            setTenants(data?.tenants);
        };
        const debounceTimer = setTimeout(fetchData, 300);
        return () => clearTimeout(debounceTimer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, sortBy, pageIndex, pageSize, activePill]);

    const sortOptions = [
        {key: "lastName", value: "Sort by Last Name"},
        {key: "firstName", value: "Sort by First Name"},
        {key: "properties", value: "Sort by Properties Count"}
    ];

    const [deleteTenant] = useMutation(DELETE_TENANT, {
        refetchQueries: [{
            query: GET_TENANTS,
            variables: { pageIndex: 0, pageSize: 10, orderAsc: true, orderField: "lastName", filter: {} }
        }],
        onCompleted: () => console.log('Tenant deleted successfully'),
        onError: (error) => {
            console.error('Error deleting tenant:', error);
            alert('Failed to delete tenant. Please try again.');
        }
    });

    const handleDelete = (id, name) => {
        if (window.confirm(`Are you sure you want to delete ${name}? This action cannot be undone.`)) {
            deleteTenant({variables: {id}});
        }
    };

    const handleDetails = (id) => navigate(`/tenants/${id}`);
    const handleEdit = (id) => navigate(`/tenants/edit/${id}`);

    if (query.loading && !tenants?.items) return (
        <div className="flex justify-center items-center h-64">
            <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
        </div>
    );

    if (query.error) return (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Error!</strong>
            <span className="block sm:inline"> {query.error.message}</span>
        </div>
    );

    const resetFilters = () => {
        setSearchTerm('');
        setSortBy('lastName');
        setActivePill('all');
        setPageIndex(0);
    };

    const handleViewModeChange = (mode) => setViewMode(mode);
    const handlePageSizeChange = (size) => { setPageSize(size); setPageIndex(0); };
    const handlePillChange = (key) => { setActivePill(key); setPageIndex(0); };

    const filterBar = (
        <FilterBar
            pills={[
                { key: 'all', label: 'All' },
                { key: 'ficaComplete', label: 'FICA Complete' },
                { key: 'ficaPending', label: 'FICA Pending' },
                { key: 'hasBanking', label: 'Has Banking' },
                { key: 'noBanking', label: 'No Banking' },
            ]}
            activePill={activePill}
            onPillChange={handlePillChange}
            onClearAll={() => { setActivePill('all'); setPageIndex(0); }}
        />
    );

    return (<ListComponent
        title="Tenants"
        headerIcon={people}
        addTitle="Tenant"
        addLink="/tenants/add"
        searchTerm={searchTerm}
        searchTermCallback={setSearchTerm}
        resetFiltersCallback={resetFilters}
        sortCallback={setSortBy}
        defaultSortOption={sortBy}
        sortOptions={sortOptions}
        viewMode={viewMode}
        viewModeCallback={handleViewModeChange}
        filterBar={filterBar}
        pageInfo={{
            totalCount: tenants?.pageInfo?.totalCount,
            totalPages: tenants?.pageInfo?.totalPages,
            pageSize: tenants?.pageInfo?.pageSize,
            pageIndex: tenants?.pageInfo?.pageIndex
        }}
        pageCallback={setPageIndex}
        pageSizeCallback={handlePageSizeChange}>

        {tenants?.items?.map(tenant => {
            const componentProps = {
                cardId: tenant.id,
                deleteCallback: () => handleDelete(tenant.id, `${tenant.firstName} ${tenant.lastName}`),
                editCallback: handleEdit,
                detailsCallback: handleDetails,
                headerImageText: `${tenant.firstName.charAt(0)}${tenant.lastName.charAt(0)}`,
                headerTitle: `${tenant.firstName} ${tenant.lastName}`,
                children: <PersonCardContentComponent
                    email={tenant.email}
                    phone={tenant.primaryPhone}
                    secondaryPhone={tenant.secondaryPhone}
                    properties={tenant.properties}
                    additionalInfo={
                        <div className="flex flex-wrap gap-2 mt-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${tenant.hasFicaDocument ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                {tenant.hasFicaDocument ? 'FICA Complete' : 'FICA Pending'}
                            </span>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${tenant.hasIdDocument ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                {tenant.hasIdDocument ? 'ID Complete' : 'ID Pending'}
                            </span>
                        </div>
                    }
                />
            };

            return viewMode === 'list' ? (
                <ListRowComponent key={tenant.id} {...componentProps} />
            ) : (
                <CardComponent key={tenant.id} {...componentProps} />
            );
        })}
    </ListComponent>);
};

export default Tenants;
